import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, Play, Download, Trash2, RefreshCw, AlertCircle,
         CheckCircle2, XCircle } from "lucide-react";
import "./AdminDashboard.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRADITION_LABELS = {
  episcopal:       "Episcopal",
  roman_catholic:  "Roman Catholic",
  anglican_acna:   "Anglican / ACNA",
  lutheran_elca:   "Lutheran (ELCA)",
  methodist_ame:   "Methodist / AME",
  reformed_pcusa:  "Reformed / PCUSA",
  generic:         "Generic",
};

const QUALITY_CATEGORY_LABELS = {
  liturgical_accuracy:      "Liturgical Accuracy",
  denominational_fidelity:  "Denominational Fidelity",
  musical_intelligence:     "Musical Intelligence",
  pastoral_practicality:    "Pastoral Practicality",
  planning_depth:           "Planning Depth",
  seasonal_integrity:       "Seasonal Integrity",
  export_quality:           "Export Quality",
  overall:                  "Overall",
};

function scoreTone(v) {
  if (v == null) return "#9ca3af";
  if (v >= 95) return "#059669";     // green
  if (v >= 85) return "#65a30d";     // lime
  if (v >= 70) return "#ca8a04";     // amber
  if (v >= 50) return "#dc2626";     // red
  return "#7f1d1d";                  // dark red
}

function fmtBytes(n) {
  if (typeof n !== "number" || n <= 0) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function ExportVerificationSummary({ traditionKey, probe }) {
  if (!probe) return (
    <div data-testid={`qa-export-summary-${traditionKey}`}
         style={{ fontSize: 11, color: "#9ca3af", fontStyle: "italic",
                   marginTop: 4 }}>
      Export not verified for this run.
    </div>
  );
  const anyVerified = ["pdf", "docx", "json"].some(
    (f) => probe[f]?.verified_at);
  const verifiedAt = anyVerified
    ? (probe.pdf?.verified_at || probe.docx?.verified_at || probe.json?.verified_at)
    : null;
  return (
    <div data-testid={`qa-export-summary-${traditionKey}`}
         style={{ marginTop: 6, padding: 6, background: "#fefce8",
                   border: "1px solid #fde68a", borderRadius: 4,
                   fontSize: 11 }}>
      <div style={{ fontWeight: 600, marginBottom: 4, color: "#78350f" }}>
        Verification Summary
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ color: "#78350f" }}>
            <th style={{ textAlign: "left", padding: "1px 4px" }}>Format</th>
            <th style={{ textAlign: "left", padding: "1px 4px" }}>Status</th>
            <th style={{ textAlign: "right", padding: "1px 4px" }}>Bytes</th>
          </tr>
        </thead>
        <tbody>
          {["pdf", "docx", "json"].map((f) => {
            const row = probe[f];
            if (row == null) return (
              <tr key={f}
                  data-testid={`qa-export-row-${traditionKey}-${f}`}>
                <td style={{ padding: "1px 4px" }}>{f.toUpperCase()}</td>
                <td style={{ padding: "1px 4px", color: "#9ca3af" }}>—</td>
                <td style={{ padding: "1px 4px", textAlign: "right",
                              color: "#9ca3af" }}>—</td>
              </tr>
            );
            const ok = !!row.ok;
            return (
              <tr key={f}
                  data-testid={`qa-export-row-${traditionKey}-${f}`}>
                <td style={{ padding: "1px 4px" }}>{f.toUpperCase()}</td>
                <td style={{ padding: "1px 4px",
                              color: ok ? "#065f46" : "#991b1b" }}>
                  {ok ? "✓ verified" : "✗ failed"}
                </td>
                <td style={{ padding: "1px 4px", textAlign: "right",
                              fontVariantNumeric: "tabular-nums" }}>
                  {fmtBytes(row.bytes)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {verifiedAt && (
        <div style={{ marginTop: 4, color: "#78350f",
                       fontVariantNumeric: "tabular-nums" }}>
          Verified: {verifiedAt}
        </div>
      )}
    </div>
  );
}

function QualityScoreCard({ tradition, scores, expandedKey, onToggle, exportProbe }) {
  if (!scores) return (<span style={{ color: "#9ca3af", fontSize: 11 }}>—</span>);
  return (
    <div data-testid={`qa-quality-${tradition}`}
         style={{ display: "flex", flexWrap: "wrap", gap: 4, maxWidth: 320 }}>
      {Object.keys(QUALITY_CATEGORY_LABELS).map((cat) => {
        const row = scores[cat];
        if (!row) return null;
        const isOpen = expandedKey === `${tradition}::${cat}`;
        const isNotVerified = row.score == null;
        const displayScore = isNotVerified ? "N/A" : row.score;
        return (
          <button
            key={cat}
            data-testid={`qa-quality-${tradition}-${cat}`}
            onClick={() => onToggle(`${tradition}::${cat}`)}
            title={isNotVerified
              ? `${QUALITY_CATEGORY_LABELS[cat]} — Not Verified (click for reasoning)`
              : `${QUALITY_CATEGORY_LABELS[cat]} — click to expand`}
            style={{ display: "inline-flex", alignItems: "center", gap: 4,
                      padding: "2px 6px", borderRadius: 4,
                      background: isOpen ? "#eef2ff"
                                : (isNotVerified ? "#f3f4f6" : "#f3f4f6"),
                      border: `1px solid ${isOpen ? "#6366f1"
                                : (isNotVerified ? "#d1d5db" : "#e5e7eb")}`,
                      fontSize: 11, cursor: "pointer",
                      fontWeight: cat === "overall" ? 700 : 500,
                      fontStyle: isNotVerified ? "italic" : "normal" }}
          >
            <span style={{ color: "#374151" }}>{QUALITY_CATEGORY_LABELS[cat]}</span>
            <span style={{ color: scoreTone(row.score),
                             fontVariantNumeric: "tabular-nums" }}>
              {displayScore}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function ReasoningPanel({ traditionKey, category, scores, exportProbe }) {
  if (!category || !scores) return null;
  const row = scores[category];
  if (!row) return null;
  const isNotVerified = row.score == null;
  const displayScore = isNotVerified ? "N/A" : `${row.score}/100`;
  return (
    <div data-testid={`qa-quality-reasoning-${traditionKey}-${category}`}
         style={{ marginTop: 6, padding: 8, background: "#fafafa",
                   borderLeft: `3px solid ${isNotVerified ? "#9ca3af" : "#6366f1"}`,
                   fontSize: 11, borderRadius: 3 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>
        {QUALITY_CATEGORY_LABELS[category]} — {displayScore}
        {isNotVerified && (
          <span data-testid={`qa-quality-badge-${traditionKey}-${category}-not-verified`}
                style={{ marginLeft: 8, padding: "1px 6px",
                          background: "#e5e7eb", color: "#4b5563",
                          borderRadius: 3, fontSize: 10,
                          fontWeight: 600, letterSpacing: 0.3 }}>
            NOT VERIFIED
          </span>
        )}
        {category === "export_quality" && row.status && !isNotVerified && (
          <span data-testid={`qa-quality-badge-${traditionKey}-export-status`}
                style={{ marginLeft: 8, padding: "1px 6px",
                          background: row.status === "verified" ? "#dcfce7"
                                     : row.status === "partial"  ? "#fef3c7"
                                     : "#fee2e2",
                          color: row.status === "verified" ? "#166534"
                                 : row.status === "partial"  ? "#92400e"
                                 : "#991b1b",
                          borderRadius: 3, fontSize: 10,
                          fontWeight: 600, letterSpacing: 0.3,
                          textTransform: "uppercase" }}>
            {row.status}
          </span>
        )}
      </div>
      {category === "export_quality" && (
        <ExportVerificationSummary traditionKey={traditionKey}
                                    probe={exportProbe} />
      )}
      {(row.reasoning || []).length === 0 && (
        <div style={{ color: "#065f46" }}>
          No deductions — this category passes cleanly.
        </div>
      )}
      {(row.reasoning || []).map((r, i) => (
        <div key={i} style={{ marginTop: 3, color: "#374151",
                                fontFamily: "ui-monospace, monospace" }}>
          {Object.entries(r).map(([k, v]) => (
            <span key={k} style={{ marginRight: 12 }}>
              <b>{k}</b>: {typeof v === "object" ? JSON.stringify(v) : String(v)}
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}


function StatusBadge({ pass, warn, label }) {
  if (pass === true) return (
    <span className="qa-badge qa-pass" data-testid={`qa-badge-${label}-pass`}>
      <CheckCircle2 size={12} /> pass
    </span>
  );
  if (pass === false && !warn) return (
    <span className="qa-badge qa-fail" data-testid={`qa-badge-${label}-fail`}>
      <XCircle size={12} /> fail
    </span>
  );
  return (
    <span className="qa-badge qa-warn" data-testid={`qa-badge-${label}-warn`}>
      <AlertCircle size={12} /> warn
    </span>
  );
}

function ProductionReadinessCard({ readiness }) {
  if (!readiness) return null;
  const { verdict, ready_count, not_ready_count, pending_count,
          blockers = [], computed_at } = readiness;
  const tone =
    verdict === "READY"     ? { bg: "#dcfce7", border: "#86efac",
                                fg: "#166534", pillBg: "#166534" } :
    verdict === "NOT_READY" ? { bg: "#fee2e2", border: "#fca5a5",
                                fg: "#991b1b", pillBg: "#991b1b" } :
                              { bg: "#fef3c7", border: "#fcd34d",
                                fg: "#78350f", pillBg: "#78350f" };
  const headline =
    verdict === "READY"     ? "READY FOR RELEASE" :
    verdict === "NOT_READY" ? "NOT READY FOR RELEASE" :
                              "PENDING — QA RUN INCOMPLETE";
  return (
    <div data-testid="qa-production-readiness"
         data-verdict={verdict}
         style={{ marginTop: 16, padding: 16, background: tone.bg,
                   border: `2px solid ${tone.border}`, borderRadius: 8 }}>
      <div style={{ display: "flex", alignItems: "center",
                     justifyContent: "space-between", gap: 12,
                     flexWrap: "wrap" }}>
        <div>
          <div data-testid="qa-readiness-verdict"
               style={{ fontSize: 18, fontWeight: 700, color: tone.fg,
                         letterSpacing: 0.5 }}>
            {headline}
          </div>
          <div style={{ marginTop: 6, fontSize: 12, color: tone.fg,
                         opacity: 0.9 }}>
            {ready_count} READY · {not_ready_count} NOT READY · {pending_count} PENDING
            {computed_at && (
              <span style={{ marginLeft: 8, opacity: 0.75 }}>
                (assessed {computed_at})
              </span>
            )}
          </div>
        </div>
        <div style={{ padding: "6px 14px", borderRadius: 999,
                       background: tone.pillBg, color: "#fff",
                       fontWeight: 700, fontSize: 12,
                       letterSpacing: 0.8 }}>
          {verdict}
        </div>
      </div>
      {blockers.length > 0 && (
        <div data-testid="qa-readiness-blockers"
             style={{ marginTop: 12, padding: 10, background: "#fff",
                       border: `1px solid ${tone.border}`,
                       borderRadius: 6, fontSize: 12 }}>
          <div style={{ fontWeight: 600, color: tone.fg,
                         marginBottom: 6 }}>
            Blockers ({blockers.length})
          </div>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {blockers.slice(0, 20).map((b, i) => (
              <li key={i} data-testid={`qa-readiness-blocker-${i}`}
                  style={{ marginBottom: 3 }}>
                <b>{b.tradition_key || "run"}</b> · <code>{b.code}</code>
                {b.detail && <> — {b.detail}</>}
              </li>
            ))}
            {blockers.length > 20 && (
              <li style={{ color: tone.fg, opacity: 0.7 }}>
                …and {blockers.length - 20} more
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

function ReadinessPill({ pr, testid }) {
  if (!pr) return <span style={{ color: "#9ca3af", fontSize: 11 }}>—</span>;
  const map = {
    READY:     { bg: "#dcfce7", fg: "#166534", label: "READY" },
    NOT_READY: { bg: "#fee2e2", fg: "#991b1b", label: "NOT READY" },
    PENDING:   { bg: "#fef3c7", fg: "#78350f", label: "PENDING" },
  };
  const t = map[pr.verdict] || map.PENDING;
  return (
    <div data-testid={testid} data-verdict={pr.verdict}>
      <span style={{ padding: "2px 8px", borderRadius: 999,
                      background: t.bg, color: t.fg,
                      fontWeight: 700, fontSize: 10,
                      letterSpacing: 0.6 }}>
        {t.label}
      </span>
      {pr.blockers_count > 0 && (
        <div style={{ marginTop: 3, fontSize: 10, color: "#991b1b" }}>
          {pr.blockers_count} blocker(s)
        </div>
      )}
    </div>
  );
}

export default function AdminAnnualPlanningQA() {
  const navigate = useNavigate();
  const [state, setState] = useState("loading");        // loading | denied | ready
  const [running, setRunning] = useState(false);
  const [runId, setRunId] = useState(null);
  const [packet, setPacket] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");                 // "start" | "purge" | ""
  const [dlAll, setDlAll] = useState(false);
  const [expandedReasoning, setExpandedReasoning] = useState(null);
  const pollRef = useRef(null);

  // Owner-gate: reuse existing admin overview endpoint (rejects non-owner).
  useEffect(() => {
    (async () => {
      try {
        await axios.get(`${API}/admin/overview`);
        setState("ready");
      } catch (e) {
        if (e.response?.status === 403 || e.response?.status === 401) {
          setState("denied");
        } else {
          setState("ready");  // network error — still show page; endpoint calls will surface real problems
        }
      }
    })();
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
  }, []);

  const fetchPacket = useCallback(async (id) => {
    try {
      const r = await axios.get(`${API}/admin/qa/annual-planning/${id}`);
      setPacket(r.data);
      if (r.data?.completed >= r.data?.total) {
        setRunning(false);
        return true;
      }
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not fetch Verification Packet.");
      setRunning(false);
      return true;
    }
    return false;
  }, []);

  const pollLoop = useCallback((id) => {
    const tick = async () => {
      const done = await fetchPacket(id);
      if (!done) pollRef.current = setTimeout(tick, 5000);
    };
    tick();
  }, [fetchPacket]);

  const start = async () => {
    setErr(""); setBusy("start"); setRunning(true); setPacket(null);
    try {
      const r = await axios.post(`${API}/admin/qa/annual-planning/start`, {});
      setRunId(r.data.run_id);
      pollLoop(r.data.run_id);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not start QA run.");
      setRunning(false);
    }
    setBusy("");
  };

  const refresh = () => { if (runId) { setRunning(true); pollLoop(runId); } };

  const download = async (traditionKey, fmt) => {
    if (!runId) return;
    const url = `${API}/admin/qa/annual-planning/${runId}/export/${traditionKey}/${fmt}`;
    try {
      const resp = await axios.get(url, { responseType: "blob" });
      const blob = new Blob([resp.data]);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `qa_${traditionKey}_${runId.slice(0, 8)}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      setErr(`Could not download ${traditionKey}.${fmt}: ${e.response?.status || e.message}`);
    }
  };

  const downloadAll = async () => {
    if (!packet?.rows) return;
    setDlAll(true); setErr("");
    for (const row of packet.rows) {
      if (row.status !== "done") continue;
      for (const fmt of ["pdf", "docx", "json"]) {
        await download(row.tradition_key, fmt);
        await new Promise((r) => setTimeout(r, 250));
      }
    }
    setDlAll(false);
  };

  const purge = async () => {
    if (!runId) return;
    if (!window.confirm(
      `Purge QA run ${runId.slice(0, 8)}?\n\n` +
      "This deletes the 7 parish_jobs and their generation_events. " +
      "This cannot be undone. Downloaded files on your computer are NOT affected."
    )) return;
    setBusy("purge"); setErr("");
    try {
      await axios.delete(`${API}/admin/qa/annual-planning/${runId}`);
      setRunId(null); setPacket(null);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not purge QA run.");
    }
    setBusy("");
  };

  if (state === "loading") return (
    <div className="fcc-loading" data-testid="qa-loading"><Loader2 className="spin" /></div>
  );
  if (state === "denied") return (
    <div className="fcc-denied" data-testid="qa-denied">
      <h1>Owner access only</h1>
      <p>The Annual Planning QA panel is restricted to the platform owner.</p>
      <button onClick={() => navigate("/dashboard")} data-testid="qa-back-to-dashboard">
        Back to Dashboard
      </button>
    </div>
  );

  const rows = packet?.rows || [];
  const done = packet?.completed || 0;
  const total = packet?.total || 7;
  const allPass = packet?.all_pass;

  return (
    <div className="fcc" data-testid="admin-annual-planning-qa">
      <div className="fcc-header" style={{ marginBottom: 16 }}>
        <div>
          <h1 style={{ margin: 0 }}>Annual Planning — Owner QA</h1>
          <p className="fcc-sub" style={{ margin: "4px 0 0", color: "#7a7a7a" }}>
            Runs the seven-tradition Annual Ministry Plan test suite against
            the live LLM without enabling the feature globally and without
            consuming customer credits.
          </p>
        </div>
        <button
          onClick={() => navigate("/admin/billing")}
          data-testid="qa-back-to-admin"
          className="fcc-btn-secondary"
        >
          Back to Admin
        </button>
      </div>

      <div className="fcc-tools" data-testid="qa-controls" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button
          onClick={start}
          disabled={running || busy === "start"}
          data-testid="qa-run-seven-tradition"
          className="fcc-btn-primary"
        >
          {(running || busy === "start")
            ? (<><Loader2 className="spin" size={14} /> Running…</>)
            : (<><Play size={14} /> Run Seven-Tradition QA</>)}
        </button>
        {runId && (
          <>
            <button onClick={refresh} disabled={running}
                    data-testid="qa-refresh"
                    className="fcc-btn-secondary">
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              onClick={downloadAll}
              disabled={running || dlAll || !rows.some((r) => r.status === "done")}
              data-testid="qa-download-all"
              className="fcc-btn-secondary"
            >
              {dlAll ? (<><Loader2 className="spin" size={14} /> Downloading…</>)
                     : (<><Download size={14} /> Download All Available Exports</>)}
            </button>
            <button
              onClick={purge}
              disabled={running || busy === "purge"}
              data-testid="qa-purge"
              className="fcc-btn-secondary"
              style={{ color: "#c53030" }}
            >
              <Trash2 size={14} /> Purge QA Run
            </button>
          </>
        )}
      </div>

      {err && (
        <div className="fcc-error" data-testid="qa-error"
             style={{ marginTop: 12, padding: 10, background: "#fff4f4", color: "#c53030",
                       borderRadius: 6, border: "1px solid #fecaca" }}>
          <AlertCircle size={14} style={{ verticalAlign: "middle" }} /> {err}
        </div>
      )}

      {runId && (
        <div className="fcc-card" data-testid="qa-progress"
             style={{ marginTop: 16, padding: 12, border: "1px solid #e5e7eb", borderRadius: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <strong data-testid="qa-run-id">Run:</strong>
            <code style={{ fontSize: 12 }}>{runId}</code>
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
              <span data-testid="qa-progress-count">{done}/{total} complete</span>
              {allPass === true && <StatusBadge pass={true}  label="overall" />}
              {allPass === false && done === total && <StatusBadge pass={false} label="overall" />}
              {done < total && <span data-testid="qa-running-pill" className="qa-badge qa-warn">
                <Loader2 className="spin" size={12} /> running
              </span>}
            </div>
          </div>
        </div>
      )}

      {packet?.production_readiness && (
        <ProductionReadinessCard readiness={packet.production_readiness} />
      )}

      {rows.length > 0 && (
        <div className="fcc-table-wrap" style={{ marginTop: 16, overflowX: "auto" }}>
          <table className="fcc-table" data-testid="qa-packet-table" style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f9fafb" }}>
                <th style={{ textAlign: "left", padding: 8 }}>Tradition</th>
                <th style={{ textAlign: "left", padding: 8 }}>Readiness</th>
                <th style={{ textAlign: "left", padding: 8 }}>Engine</th>
                <th style={{ textAlign: "left", padding: 8 }}>Validation</th>
                <th style={{ textAlign: "left", padding: 8 }}>Months</th>
                <th style={{ textAlign: "left", padding: 8 }}>Structure</th>
                <th style={{ textAlign: "left", padding: 8 }}>Depth</th>
                <th style={{ textAlign: "left", padding: 8 }}>Leakage</th>
                <th style={{ textAlign: "left", padding: 8 }}>AI Quality</th>
                <th style={{ textAlign: "left", padding: 8 }}>Exports</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.tradition_key}
                    data-testid={`qa-row-${r.tradition_key}`}
                    style={{ borderTop: "1px solid #e5e7eb" }}>
                  <td style={{ padding: 8 }}>
                    <div>{TRADITION_LABELS[r.tradition_key] || r.tradition_key}</div>
                    {r.subtradition && (
                      <div style={{ fontSize: 11, color: "#6b7280" }}>
                        subtradition: {r.subtradition}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: 8 }}>
                    <ReadinessPill pr={r.production_readiness}
                                    testid={`qa-readiness-${r.tradition_key}`} />
                  </td>
                  <td style={{ padding: 8, fontSize: 12 }}>{r.tradition_engine || "—"}</td>
                  <td style={{ padding: 8, fontSize: 12 }}>{r.tradition_validation_status || "—"}</td>
                  <td style={{ padding: 8 }} data-testid={`qa-months-${r.tradition_key}`}>
                    {r.months_count ?? "—"}
                  </td>
                  <td style={{ padding: 8 }}>
                    <StatusBadge pass={r.structure_pass} label={`struct-${r.tradition_key}`} />
                  </td>
                  <td style={{ padding: 8 }}>
                    <StatusBadge pass={r.content_depth_pass} label={`depth-${r.tradition_key}`} />
                    {r.content_depth_codes?.length > 0 && (
                      <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                        {r.content_depth_codes.slice(0, 3).join(", ")}
                        {r.content_depth_codes.length > 3 && ` (+${r.content_depth_codes.length - 3})`}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: 8 }} data-testid={`qa-leakage-${r.tradition_key}`}>
                    <StatusBadge
                      pass={(r.leakage_codes?.length === 0)
                             && (r.compliance_claim_codes?.length === 0)
                             && r.status === "done"}
                      label={`leak-${r.tradition_key}`}
                    />
                  </td>
                  <td style={{ padding: 8, verticalAlign: "top" }}>
                    <QualityScoreCard
                      tradition={r.tradition_key}
                      scores={r.quality_scores}
                      exportProbe={r.export_probe}
                      expandedKey={expandedReasoning}
                      onToggle={(k) => setExpandedReasoning(
                        expandedReasoning === k ? null : k)}
                    />
                    {expandedReasoning?.startsWith(`${r.tradition_key}::`) && (
                      <ReasoningPanel
                        traditionKey={r.tradition_key}
                        category={expandedReasoning.split("::")[1]}
                        scores={r.quality_scores}
                        exportProbe={r.export_probe}
                      />
                    )}
                  </td>
                  <td style={{ padding: 8, whiteSpace: "nowrap" }}>
                    {["pdf", "docx", "json"].map((f) => (
                      <button
                        key={f}
                        disabled={r.status !== "done"}
                        onClick={() => download(r.tradition_key, f)}
                        data-testid={`qa-dl-${r.tradition_key}-${f}`}
                        className="fcc-btn-mini"
                        style={{ marginRight: 4, padding: "4px 8px",
                                  fontSize: 11, borderRadius: 4,
                                  border: "1px solid #d1d5db",
                                  cursor: r.status === "done" ? "pointer" : "not-allowed",
                                  opacity: r.status === "done" ? 1 : 0.5 }}
                      >
                        {f.toUpperCase()}
                      </button>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rows.length === 0 && !runId && (
        <div style={{ marginTop: 24, padding: 24, textAlign: "center",
                       color: "#6b7280", background: "#f9fafb", borderRadius: 8 }}
             data-testid="qa-empty">
          <p>No QA runs yet. Click <strong>Run Seven-Tradition QA</strong> to start.</p>
          <p style={{ fontSize: 12 }}>
            The global feature flag remains <code>ANNUAL_PLANNING_ENGINE_ENABLED=0</code>.
            Customer credit consumption is zero for owner QA jobs.
          </p>
        </div>
      )}
    </div>
  );
}
